"use client";

import Image from "next/image";
import { Phone } from "lucide-react";
import { CtaButton } from "@/components/layout/CtaButton";
import { Reveal } from "@/components/ui/Reveal";

export type BestSellerCtaSectionProps = {
  eyebrow?: string;
  heading?: string;
  paragraphs?: string[];
  primaryCta?: { label: string; href?: string; opensQuoteModal?: boolean };
  secondaryCta?: { label: string; href: string };
  backgroundImageSrc?: string;
  className?: string;
};

const defaultParagraphs = [
  "Most authors reach a point where the story feels bigger than their skillset. That's where real support matters. Our team combines years of book publishing experience with the guidance of industry experienced book publishing consultants, so you're never navigating blind.",
  "You get book editors, book cover designers, and seasoned book marketers who know exactly where you readers are residing. Ready to work with professionals who know book publishers industry in & out?",
];

export function BestSellerCtaSection({
  eyebrow = "Ready To Make Your Book",
  heading = "This Season's Best Seller?",
  paragraphs = defaultParagraphs,
  primaryCta = { label: "Meet Your Publishing Team", opensQuoteModal: true },
  secondaryCta = { label: "Call Us Now", href: "tel:+18556055210" },
  backgroundImageSrc = "/landing/cta/banner-bg.webp",
  className,
}: BestSellerCtaSectionProps) {
  return (
    <section
      className={[
        "relative overflow-hidden bg-[#1a1a1a] bg-cover bg-center py-12 lg:py-[50px]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        backgroundImage: `linear-gradient(rgba(20,16,12,0.72), rgba(20,16,12,0.78)), url('${backgroundImageSrc}')`,
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-[#b46c29]/35 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-8">
          <div className="text-white">
            <Reveal delayMs={0}>
              <p className="font-heading text-2xl font-semibold leading-tight sm:text-[28px]">
                {eyebrow}
              </p>
            </Reveal>

            <Reveal delayMs={150}>
              <h2 className="mt-1 font-heading text-4xl font-bold leading-tight text-white sm:text-[45px] sm:leading-[54px]">
                {heading}
              </h2>
            </Reveal>

            {paragraphs.map((paragraph, index) => (
              <Reveal key={paragraph.slice(0, 24)} delayMs={300 + index * 150}>
                <p
                  className={[
                    "text-base leading-relaxed text-white/95",
                    index === 0 ? "mt-4" : "mt-3",
                  ].join(" ")}
                >
                  {paragraph}
                </p>
              </Reveal>
            ))}

            <Reveal delayMs={600}>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {primaryCta.opensQuoteModal || !primaryCta.href ? (
                  <CtaButton opensQuoteModal variant="green" size="md">
                    {primaryCta.label}
                  </CtaButton>
                ) : (
                  <CtaButton href={primaryCta.href} variant="green" size="md">
                    {primaryCta.label}
                  </CtaButton>
                )}
                <CtaButton
                  href={secondaryCta.href}
                  variant="outline"
                  size="md"
                  className="border-2 border-[#b46c29] bg-white text-[#b46c29] hover:bg-[#b46c29] hover:text-white"
                  icon={<Phone className="h-4 w-4" />}
                >
                  {secondaryCta.label}
                </CtaButton>
              </div>
            </Reveal>
          </div>

          <Reveal variant="zoom" delayMs={200} className="relative mx-auto w-full max-w-lg">
            <div className="relative mx-auto flex h-[360px] w-full max-w-md items-end justify-center sm:h-[400px]">
              <Image
                src="/landing/cta/book-popcorn.webp"
                alt="Larryn and Alaya's Popcorn Adventure book cover"
                width={170}
                height={255}
                sizes="170px"
                quality={70}
                loading="lazy"
                fetchPriority="low"
                className="animate-float absolute left-[4%] top-[8%] z-10 w-[38%] max-w-[170px] rotate-[-8deg] drop-shadow-2xl"
              />
              <Image
                src="/landing/cta/book-barnaby.webp"
                alt="Barnaby the One-Eared Bunny book cover"
                width={160}
                height={240}
                sizes="160px"
                quality={70}
                loading="lazy"
                fetchPriority="low"
                className="animate-float-slow absolute right-[6%] top-[2%] z-0 w-[36%] max-w-[160px] rotate-[10deg] drop-shadow-xl"
              />
              <Image
                src="/landing/cta/book-denali.webp"
                alt="The Legend of Denali book cover"
                width={200}
                height={300}
                sizes="200px"
                quality={70}
                loading="lazy"
                fetchPriority="low"
                className="animate-float-slower relative z-20 mb-2 w-[46%] max-w-[200px] rotate-[4deg] drop-shadow-2xl"
              />

              <Image
                src="/landing/free-c.png"
                alt="Free consultation, no obligation"
                width={115}
                height={115}
                sizes="115px"
                quality={60}
                loading="lazy"
                fetchPriority="low"
                className="absolute bottom-0 left-[8%] z-30 h-[100px] w-[100px] object-contain sm:h-[115px] sm:w-[115px]"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
